import { createNoteRequestSchema, noteResponseSchema, type NoteResponse } from '@emr/shared';
import { z } from 'zod';

import { apiRequest } from './client.ts';

/**
 * Clinical notes and attestation (`server/src/clinical/notesRoutes.ts`).
 *
 * **Not wrapped**, the same finding as `api/encounters.ts` and for the identical reason:
 * `notesRoutes.ts`'s `respond()` also returns `c.json(result.value, successStatus)` directly, and
 * `notesService.ts` types every single-note function (`createNote`, `getNote`, `updateNote`,
 * `signNote`, `cosignNote`) as `Promise<NoteResult<NoteResponse>>` and `listNotes` as
 * `Promise<NoteResult<readonly NoteResponse[]>>` — a bare object or array on the wire, never
 * `{ note: ... }` / `{ notes: [...] }`.
 *
 * `updateNote` (`PATCH /api/notes/:noteId`, editing a draft's content) is not wired here — the
 * composer creates a note in one step and this build does not offer editing a draft afterward. It
 * would use the same bare-`NoteResponse` shape as `signNote`/`cosignNote` below if added.
 */

export async function listNotes(
  patientId: string,
  signal?: AbortSignal,
): Promise<readonly NoteResponse[]> {
  return apiRequest(
    `/api/patients/${patientId}/notes`,
    z.array(noteResponseSchema),
    signal === undefined ? {} : { signal },
  );
}

export interface CreateNoteInput {
  readonly noteType: string;
  readonly content: string;
  /** `undefined` for a freestanding note with no formal encounter behind it. */
  readonly encounterId: string | undefined;
}

/** `POST /api/patients/:patientId/notes` — create a draft, requiring `chart:write`. */
export function createNote(patientId: string, input: CreateNoteInput): Promise<NoteResponse> {
  const body = createNoteRequestSchema.parse({
    noteType: input.noteType,
    content: input.content,
    ...(input.encounterId === undefined ? {} : { encounterId: input.encounterId }),
  });
  return apiRequest(`/api/patients/${patientId}/notes`, noteResponseSchema, {
    method: 'POST',
    body,
  });
}

/** `POST /api/notes/:noteId/sign` — self-attestation, requiring `note:sign`. Author-only, server-enforced. */
export function signNote(noteId: string): Promise<NoteResponse> {
  return apiRequest(`/api/notes/${noteId}/sign`, noteResponseSchema, { method: 'POST' });
}

/** `POST /api/notes/:noteId/cosign` — supervisory attestation, requiring `note:cosign` (physician only). */
export function cosignNote(noteId: string): Promise<NoteResponse> {
  return apiRequest(`/api/notes/${noteId}/cosign`, noteResponseSchema, { method: 'POST' });
}
